import { useCallback } from "react";
import { FileBarChart, FileSpreadsheet } from "lucide-react";

import { useCustomReports, useStandardReports } from "@/hooks/useCustomReports";
import { isPrintSummary } from "@/lib/report-builder/print-summaries";

import type { AppRoute } from "@/lib/routes";

/** The "My reports" sidebar row's path: a group key, redirecting to the landing. */
export const MY_REPORTS_PATH = "/reports/custom";
/** The "Standard reports" sidebar row's path, likewise a group key. */
export const STANDARD_REPORTS_PATH = "/reports/standard";
/** The "My print summaries" sidebar row's path, likewise a group key. */
export const MY_PRINT_SUMMARIES_PATH = "/reports/print-summaries";

/** The Overview's tabs, in order: one per group of reports (`ReportsPage`). */
export const REPORTS_TABS = [
  { key: "standard", label: "Standard reports" },
  { key: "mine", label: "My reports" },
  { key: "print-summaries", label: "My print summaries" },
  { key: "catalog", label: "Catalog" },
] as const;

export type ReportsTab = (typeof REPORTS_TABS)[number]["key"];

/** The Overview open on one group's tab: where a sidebar group row sends the reader. */
export function reportsTabPath(tab: ReportsTab): string {
  return `/reports?tab=${tab}`;
}

export function customReportPath(id: string): string {
  return `${MY_REPORTS_PATH}/${id}`;
}

/**
 * The Reports sidebar's live rows: the tenant's standard reports under
 * "Standard reports", in the server's order, the caller's own under "My
 * reports", and the print summaries they saved under "My print summaries",
 * each A–Z by name. A `useRows` hook (`ModuleSection.useRows`), because
 * the lists are the server's and a module declaration is evaluated once.
 *
 * The rows are sidebar decoration; the route they link to,
 * `/reports/custom/:id`, is registered once as a hidden route.
 */
export function useMyReportRows(): (path: string) => AppRoute[] | undefined {
  const { data: own } = useCustomReports();
  const { data: standard } = useStandardReports();
  return useCallback(
    (path: string) => {
      const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
      const rows =
        path === MY_REPORTS_PATH && own
          ? own.filter((r) => !isPrintSummary(r)).sort(byName)
          : path === MY_PRINT_SUMMARIES_PATH && own
            ? own.filter(isPrintSummary).sort(byName)
            : path === STANDARD_REPORTS_PATH
              ? standard
              : undefined;
      return rows?.map((r) => ({
        path: customReportPath(r.id),
        label: r.name,
        icon: r.format === "spreadsheet" ? FileSpreadsheet : FileBarChart,
        status: "live" as const,
        element: <></>,
        requiredFeature: "report_builder" as const,
      }));
    },
    [own, standard],
  );
}
